import { AnyAction, Middleware } from '@reduxjs/toolkit';

const SESSION_BOUNDARY_ACTIONS = new Set([
  'auth/logout/fulfilled',
  'auth/login/fulfilled',
  'auth/register/fulfilled',
]);

const isPending = (action: AnyAction) => action.type.endsWith('/pending');
const isSettled = (action: AnyAction) => (
  action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected')
);

/**
 * Async work started for one account must never write into another account's
 * Redux state. Track the session generation that owns each thunk request and
 * discard late fulfillments/rejections after logout, login, or registration.
 */
export const createSessionBoundaryMiddleware = (): Middleware => {
  let sessionGeneration = 0;
  const requestGenerations = new Map<string, number>();

  return () => next => (unknownAction) => {
    const action = unknownAction as AnyAction;
    const requestId = action.meta?.requestId;

    // Authenticate the action against the session that started it before
    // accepting a fulfilled login/register boundary. Otherwise an older login
    // response could replace a newer authenticated session.
    if (typeof requestId === 'string' && isSettled(action)) {
      const requestGeneration = requestGenerations.get(requestId);
      requestGenerations.delete(requestId);
      if (
        requestGeneration !== undefined
        && requestGeneration !== sessionGeneration
      ) {
        return action;
      }
    }

    if (SESSION_BOUNDARY_ACTIONS.has(action.type)) {
      sessionGeneration += 1;
      return next(action);
    }

    if (typeof requestId === 'string' && isPending(action)) {
      requestGenerations.set(requestId, sessionGeneration);
    }

    return next(action);
  };
};

export default createSessionBoundaryMiddleware;
